/**
 * Notification Service API client → talks to the standalone Notification Service.
 * Base URL: /notifications/api/v1/notifications
 *
 * REST calls use the same JWT refresh/retry semantics as ``request()`` in core.ts.
 * SSE uses fetch + ReadableStream (native EventSource cannot send Authorization),
 * mirroring the Migration Studio / documents streaming pattern.
 */

import { config } from '../../config'
import { getCurrentTenantId } from '../../contexts/TenantContext'
import { logger } from '../../utils/logger'
import { ApiError, isTokenExpiringSoon, refreshAccessToken } from './core'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const BASE = () => `${config.api.notificationsUrl}/api/v1/notifications`
const notifServiceFetch = createServiceFetch(config.api.notificationsUrl)

export interface NotificationItem {
  id: string
  tenant_id?: string
  category: string
  title: string
  body?: string | null
  entity_type?: string | null
  entity_id?: string | null
  deep_link?: string | null
  priority: string
  icon?: string | null
  actor_type?: string | null
  actor_id?: string | null
  data?: Record<string, unknown>
  seen: boolean
  read: boolean
  created_at?: string | null
}

export interface NotificationListResponse {
  items: NotificationItem[]
  next_cursor?: string | null
}

export interface NotificationCounts {
  unread: number
  unseen: number
}

export type EmailCategoryKey =
  | 'workflow_run'
  | 'cwb_complete'
  | 'aba_complete'
  | 'agent_response'
  | 'access_decision'
  | 'user_registration'
  | 'benchmark_assignment'
  | 'knowledge_config'

export interface NotificationPreferences {
  in_app_enabled: boolean
  sound_enabled: boolean
  email_enabled: boolean
  email_categories: Record<EmailCategoryKey, boolean>
}

export type NotificationPreferencesUpdate = Partial<{
  in_app_enabled: boolean
  sound_enabled: boolean
  email_enabled: boolean
  email_categories: Partial<Record<EmailCategoryKey, boolean>>
}>

function authHeaders(accessToken: string | null, extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(extra || {}) }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const tenantId = getCurrentTenantId()
  if (tenantId) headers['X-Tenant-ID'] = tenantId
  return headers
}

function readAccessToken(): string | null {
  try {
    return localStorage.getItem(config.auth.tokenKey)
  } catch {
    return null
  }
}

/**
 * Notification REST helper with core.ts-style proactive refresh + 401 retry.
 * SSE stream connections intentionally keep a lighter path (see subscribeToNotifications).
 */
async function notifFetch(url: string, init?: RequestInit): Promise<Response> {
  let token = readAccessToken()
  if (token && isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken()
    } catch {
      // Proceed with current token; 401 path below may still refresh.
    }
  }

  let response = await notifServiceFetch(url, {
    ...init,
    headers: authHeaders(token, init?.headers as Record<string, string> | undefined),
  })

  if (response.status === 401) {
    try {
      const newToken = await refreshAccessToken()
      response = await notifServiceFetch(url, {
        ...init,
        headers: authHeaders(newToken, init?.headers as Record<string, string> | undefined),
      })
    } catch {
      // Return original 401; refreshAccessToken dispatches auth:token-expired when appropriate.
    }
  }

  return response
}

async function asJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    if (resp.status === 503) {
      logger.warn('Notification service unavailable')
      throw new ApiError('Notification service unavailable', 503)
    }
    throw new ApiError(`Notification API error ${resp.status}`, resp.status)
  }
  return (await resp.json()) as T
}

export const notificationsApi = {
  async list(params?: { category?: string; unread?: boolean; before?: string; limit?: number }): Promise<NotificationListResponse> {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.unread) qs.set('unread', 'true')
    if (params?.before) qs.set('before', params.before)
    if (params?.limit) qs.set('limit', String(params.limit))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    const resp = await notifFetch(`${BASE()}${suffix}`, { method: 'GET' })
    return asJson<NotificationListResponse>(resp)
  },

  async counts(): Promise<NotificationCounts> {
    const resp = await notifFetch(`${BASE()}/unread-count`, { method: 'GET' })
    return asJson<NotificationCounts>(resp)
  },

  async markRead(id: string): Promise<NotificationCounts> {
    const resp = await notifFetch(`${BASE()}/${encodeURIComponent(id)}/read`, {
      method: 'POST',
    })
    return asJson<NotificationCounts>(resp)
  },

  async markAllRead(): Promise<void> {
    await notifFetch(`${BASE()}/read-all`, { method: 'POST' })
  },

  async markSeen(): Promise<void> {
    await notifFetch(`${BASE()}/seen`, { method: 'POST' })
  },

  async dismiss(id: string): Promise<void> {
    await notifFetch(`${BASE()}/${encodeURIComponent(id)}`, { method: 'DELETE' })
  },

  async getPreferences(): Promise<NotificationPreferences> {
    const resp = await notifFetch(`${BASE()}/preferences`, { method: 'GET' })
    return asJson<NotificationPreferences>(resp)
  },

  async updatePreferences(update: NotificationPreferencesUpdate): Promise<NotificationPreferences> {
    const resp = await notifFetch(`${BASE()}/preferences`, {
      method: 'PUT',
      body: JSON.stringify(update),
    })
    return asJson<NotificationPreferences>(resp)
  },
}

export interface NotificationSnapshot {
  items: NotificationItem[]
  unread: number
  unseen: number
}

export interface NotificationStreamConnection {
  close: () => void
}

const STREAM_RETRY_INITIAL_MS = 1000
const STREAM_RETRY_MAX_MS = 30_000
/** nginx limit_conn / rate-limit: wait longer so reconnects do not amplify 429s. */
const STREAM_RETRY_429_MS = 45_000
/** Service down / overloaded: same long cool-down as 429 so we do not storm the bell. */
const STREAM_RETRY_503_MS = 45_000

/**
 * Subscribe to the per-user notification SSE stream with Bearer auth.
 * Auto-reconnects with backoff until `close()` is called.
 * HTTP 429 uses a longer fixed backoff so multi-tab reconnect storms cool down.
 */
export function subscribeToNotifications(callbacks: {
  onSnapshot?: (snapshot: NotificationSnapshot) => void
  onNotification?: (item: NotificationItem) => void
  onOpen?: () => void
  onError?: () => void
}): NotificationStreamConnection {
  const url = `${BASE()}/stream`
  let controller: AbortController | null = null
  let isClosed = false
  let retryDelay = STREAM_RETRY_INITIAL_MS

  const processSseLines = (
    lines: string[],
    state: { event: string; data: string },
  ) => {
    for (const raw of lines) {
      const line = raw.replace(/\r$/, '')
      if (line.startsWith(':')) continue
      if (line === '') {
        const data = state.data
        const eventName = state.event
        state.data = ''
        state.event = ''
        if (!data || isClosed) continue
        if (eventName === 'ping') continue
        try {
          const parsed = JSON.parse(data)
          if (eventName === 'snapshot') {
            callbacks.onSnapshot?.(parsed as NotificationSnapshot)
          } else {
            callbacks.onNotification?.(parsed as NotificationItem)
          }
        } catch {
          /* heartbeat or non-JSON */
        }
        continue
      }
      if (line.startsWith('event:')) {
        state.event = line.slice(6).trim()
        continue
      }
      if (line.startsWith('data:')) {
        const chunk = line.slice(5).trimStart()
        state.data = state.data ? `${state.data}\n${chunk}` : chunk
      }
    }
  }

  const openStream = async (token: string | null): Promise<Response> => {
    return notifServiceFetch(url, {
      method: 'GET',
      headers: authHeaders(token, { Accept: 'text/event-stream' }),
      signal: controller!.signal,
    })
  }

  const run = async () => {
    while (!isClosed) {
      controller = new AbortController()
      let lastStatus: number | null = null
      try {
        let token = readAccessToken()
        if (token && isTokenExpiringSoon(token)) {
          try {
            token = await refreshAccessToken()
          } catch {
            // Continue with current token; 401 path below may still refresh.
          }
        }
        let response = await openStream(token)
        // Mirror REST notifFetch: one refresh+retry on 401 before backoff.
        if (response.status === 401) {
          try {
            const newToken = await refreshAccessToken()
            response = await openStream(newToken)
          } catch {
            // refreshAccessToken dispatches auth:token-expired when appropriate.
          }
        }
        lastStatus = response.status
        if (!response.ok || !response.body) {
          if (!isClosed) callbacks.onError?.()
          throw new Error(`stream status ${response.status}`)
        }
        callbacks.onOpen?.()
        retryDelay = STREAM_RETRY_INITIAL_MS
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        const state = { event: '', data: '' }
        while (!isClosed) {
          const { done, value } = await reader.read()
          if (value) {
            buffer += decoder.decode(value, { stream: !done })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''
            processSseLines(lines, state)
          }
          if (done) break
        }
      } catch (err) {
        if (isClosed) return
        if (err instanceof Error && err.name === 'AbortError') return
        callbacks.onError?.()
      }
      if (isClosed) return
      // Reconnect with capped exponential backoff; 429/503 wait longer to cool nginx / outages.
      const longBackoffMs =
        lastStatus === 429 ? STREAM_RETRY_429_MS : lastStatus === 503 ? STREAM_RETRY_503_MS : null
      const waitMs = longBackoffMs ?? retryDelay
      await new Promise((r) => setTimeout(r, waitMs))
      retryDelay = longBackoffMs ?? Math.min(retryDelay * 2, STREAM_RETRY_MAX_MS)
    }
  }

  void run()

  return {
    close: () => {
      isClosed = true
      controller?.abort()
    },
  }
}
